/**
 * ⟦C57 · lot 5d · A-174⟧ **Les dessous de la Bibliothèque** : tout ce que l'écran ne dit plus.
 *
 * La Bibliothèque garde un tableau, quelques chiffres et des phrases de 12 mots au plus. Ce qui s'y
 * lisait avant — pourquoi la rente existe, ce que coûte une nulle de trop, ce que fait une série, ce
 * que le moteur ne branche pas encore — est ici, mot pour mot, derrière « 📰 Les dessous ». Rien
 * n'est supprimé : les chiffres viennent des mêmes constantes du moteur que l'écran, et les phrases
 * sont celles d'avant le chantier.
 */

import type { ReactNode } from "react";

import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { nombre } from "../../game/format";

import type { RenteChaine } from "./rente";
import { multiplicateur, multiplicateurSerie, pourcentage } from "./textes-courts";

export interface DessousBibliothequeProps {
  rente: RenteChaine;
  /** Au-delà de ce nombre d'evergreens actives, les rendements décroissent (`RENTE_SATURATION_RANG`). */
  seuilSaturation: number;
  nullesVisibles: number;
  /** Les nulles au-delà du seuil toléré. */
  enTrop: number;
  /** La conversion actuelle, telle que `malusNulles` la rend. */
  malusActuel: number;
  /** Le plancher de conversion (`PLANCHER_NULLES`). */
  plancher: number;
  /** Le seuil de nulles tolérées, déduit de `malusNulles`. */
  seuilNulles: number;
  /** Ce que coûte une nulle de trop (déduit de `malusNulles`), en fraction. */
  coutParNulle: number;
  /** `RENTE_MULT_SERIE`. */
  multSerie: number;
  /** Le minimum de vidéos par série. */
  serieMin: number;
  peutFaireLeMenage: boolean;
  peutRangerEnSerie: boolean;
}

export function DessousBibliotheque(p: DessousBibliothequeProps) {
  const { rente } = p;
  const evergreens = rente.lignes.length;

  const ligneRente: ReactNode =
    evergreens > 0 ? (
      <>
        🌲 {evergreens} evergreen{evergreens > 1 ? "s" : ""} active{evergreens > 1 ? "s" : ""} —
        elles rapportent {nombre(rente.vues)} vues, {nombre(rente.abonnes)} abonné
        {rente.abonnes >= 2 ? "s" : ""}
        {rente.monetise ? ` et ${nombre(rente.argent)} ₵` : ""} par semaine simulée,{" "}
        <em>pendant que tu dors</em>.
      </>
    ) : (
      <>🌲 Aucune pour l'instant. Une evergreen rapporte encore longtemps après sa sortie.</>
    );

  return (
    <LesDessous titre="Lire la bibliothèque">
      <DessousBloc titre="La rente">
        <p className="dessous-texte">{ligneRente}</p>
        {rente.saturees > 0 ? (
          <p className="dessous-texte">
            Au-delà de {p.seuilSaturation} actives, les rendements décroissent : {rente.saturees}{" "}
            vidéo{rente.saturees > 1 ? "s sont retenues" : " est retenue"}.
          </p>
        ) : null}
        {rente.facteurPause < 1 ? (
          <p className="dessous-texte">
            Pause annoncée : la rente est à {pourcentage(rente.facteurPause)}.
          </p>
        ) : null}
      </DessousBloc>

      {p.nullesVisibles > 0 ? (
        <DessousBloc titre="Le ménage">
          <p className="dessous-texte">
            💀 {p.nullesVisibles} nulle{p.nullesVisibles > 1 ? "s" : ""} visible
            {p.nullesVisibles > 1 ? "s" : ""}
            {p.enTrop > 0 ? (
              <>
                , dont {p.enTrop} de trop : ta conversion est à{" "}
                <strong>{multiplicateur(p.malusActuel)}</strong> sur toutes tes <em>prochaines</em>{" "}
                publications
                {p.malusActuel <= p.plancher ? (
                  <> — au plancher de {multiplicateur(p.plancher)}, ça ne descendra pas plus bas</>
                ) : (
                  <>
                    {" "}
                    — {pourcentage(p.coutParNulle)} par nulle au-delà de {p.seuilNulles}
                  </>
                )}
                .{" "}
                {p.peutFaireLeMenage
                  ? "Le ménage est la seule sortie : passe-les en privé."
                  : "Le nœud Gestion d'image débloque le ménage — la seule sortie."}
              </>
            ) : (
              <> — {p.seuilNulles} sont tolérées, ta conversion est intacte.</>
            )}
          </p>
          <p className="dessous-texte">
            Une nulle laissée en ligne pèse sur la conversion de <em>toute</em> la chaîne. La passer
            en privé, c'est la seule sortie : elle ne compte plus parmi tes nulles visibles, et elle
            n'encaisse plus rien non plus.
          </p>
          {!p.peutFaireLeMenage ? (
            <p className="dessous-texte">
              Le nœud <strong>Gestion d'image</strong> de l'arbre débloque le ménage — c'est lui qui
              permet de retirer une nulle de la chaîne.
            </p>
          ) : null}
        </DessousBloc>
      ) : null}

      <DessousBloc titre="Les séries">
        <p className="dessous-texte">
          Ranger {p.serieMin} vidéos ou plus sous un même nom fait circuler l'audience de l'une à
          l'autre : la rente de leurs evergreens est multipliée par {multiplicateurSerie(p.multSerie).slice(1)}.
        </p>
        {!p.peutRangerEnSerie ? (
          <p className="dessous-texte">
            Le nœud <strong>Lore de chaîne</strong> de l'arbre débloque les séries.
          </p>
        ) : null}
      </DessousBloc>

      <DessousBloc titre="Pas encore branché">
        <DessousLignes
          lignes={[
            {
              icone: "⚙",
              texte:
                "Remasteriser : tu relances la demi-vie d'une evergreen, qui repart pour un tour de rente, à pleine force. Le moteur facture déjà l'action mais ne réarme pas encore la demi-vie : pour l'instant, tu paierais pour rien.",
            },
            {
              icone: "⚙",
              texte:
                "Ranger en série : le moteur valide et facture, mais ne crée pas encore la série.",
            },
          ]}
        />
      </DessousBloc>

      <DessousBloc>
        <p className="dessous-texte">
          Clin d'œil de la maison : le vrai pro n'a laissé que 21 longues visibles.
        </p>
      </DessousBloc>
    </LesDessous>
  );
}
